// userProfile.js - Profile image code removed
import React, { useEffect, useState } from "react";
import {
  Container,
  Card,
  Alert,
  CardBody,
  Button,
  Label,
  Form,
  Input,
  InputGroup,
  InputGroupText,
  FormFeedback,
  Tooltip,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { fetchProfile, editUser } from "../../slices/thunks";
import { resetProfileFlag } from "../../slices/auth/profile/reducer";
import NameInput from "../../common/NameInput";
import EmailInput from "../../common/EmailInput";
import PasswordInput from "../../common/PasswordInput";
import BreadCrumb from "../../Components/Common/BreadCrumb";

const UserProfile = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { user, success, error, loading } = useSelector((state) => state.Profile);
  const [formValues, setFormValues] = useState(null);
  const [passwordShow, setPasswordShow] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchProfile());

    return () => {
      dispatch(resetProfileFlag());
    };
  }, [dispatch]);

  useEffect(() => {
    if (user) {
      console.log("User data received:", user);
      
      setFormValues({
        firstname: user.firstname || "",
        lastname: user.lastname || "",
        email: user.email || "",
        password: "",
      });
    }
  }, [user]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: formValues || {
      firstname: "",
      lastname: "",
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      firstname: Yup.string().required("First name is required"),
      lastname: Yup.string().required("Last name is required"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      password: Yup.string()
        .nullable()
        .notRequired()
        .test(
          "password-validation",
          "Password must be at least 8 characters with uppercase, lowercase, number, and special character",
          function (value) {
            if (!value || value.trim() === "" || value.includes("$")) {
              return true;
            }
            if (value.length < 8) {
              return this.createError({ message: "Password must be at least 8 characters" });
            }
            if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/.test(value)) {
              return this.createError({
                message: "Password must contain at least one uppercase, one lowercase, one number, and one special character"
              });
            }
            return true;
          }
        ),
    }),
    onSubmit: async (values) => {
      const payload = {
        firstname: values.firstname,
        lastname: values.lastname,
        email: values.email,
      };

      if (values.password && values.password.trim() !== "" && !values.password.includes('$')) {
        payload.password = values.password;
      }

      await dispatch(editUser(payload));
      dispatch(fetchProfile());
      
      if (values.password && !values.password.includes('$')) {
        setTimeout(() => {
          validation.setFieldValue("password", "");
        }, 100);
      }
    },
  });

  if (!formValues) return <Alert color="info">Loading profile...</Alert>;

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <BreadCrumb title='Edit Profile' pageTitle='Profile' />
        </Container>
      </div>
      <div className="table-container" style={{ marginTop: "15px" }}>
        <Card>
          <CardBody>
            <Form onSubmit={validation.handleSubmit}>
              <NameInput
                name="firstname"
                id="firstname"
                label="First Name"
                required={true}
                value={validation?.values?.firstname}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                error={validation?.touched?.firstname && validation?.errors?.firstname}
              />

              <NameInput
                name="lastname"
                id="lastname"
                label="Last Name"
                required={true}
                value={validation?.values?.lastname}
                onChange={validation.handleChange}
                onBlur={validation?.handleBlur}
                error={validation?.touched?.lastname && validation?.errors?.lastname}
              />

              <EmailInput
                name="email"
                id="email"
                required={true}
                layout="below"
                showHelp={false}
                disabled={true}
                value={validation?.values?.email}
              />

              {/* Password field */}
              <div className="mb-3">
                <Label htmlFor="password" className="form-label d-flex align-items-center">
                  <span>Password</span>
                  <span
                    id="passwordTooltip"
                    className="ms-1"
                    style={{ position: "relative", top: "-2px", cursor: "pointer" }}
                  >
                    <i className="ri-question-line" style={{ fontSize: '16px', color: '#6c757d' }}></i>
                  </span>
                </Label>
                <Tooltip
                  placement="right"
                  isOpen={tooltipOpen}
                  target="passwordTooltip"
                  toggle={() => setTooltipOpen(!tooltipOpen)}
                >
                  Password field is currently disabled for security reasons.
                </Tooltip>
                <div className="position-relative">
                  <InputGroup>
                    <Input
                      type={passwordShow ? "text" : "password"}
                      name="password"
                      id="password"
                      className="form-control pe-5"
                      placeholder="Password field is disabled"
                      value=""
                      disabled
                      style={{
                        backgroundColor: "#f8f9fa",
                        cursor: "not-allowed",
                        opacity: 0.6
                      }}
                    />
                    <InputGroupText
                      style={{ 
                        cursor: "not-allowed", 
                        backgroundColor: "#f8f9fa", 
                        borderLeft: "none",
                        opacity: 0.6
                      }}
                      className="border-start-0"
                    >
                      <i
                        className={passwordShow ? "ri-eye-off-fill align-middle" : "ri-eye-fill align-middle"}
                        style={{
                          color: "#6c757d"
                        }}
                      ></i>
                    </InputGroupText>
                  </InputGroup>
                </div>
                <div className="text-muted mt-1" style={{ fontSize: "0.875em" }}>
                  <i className="ri-information-line"></i> Password field is currently disabled for security reasons.
                </div>
              </div>
              
              <div className="text-center mt-4">
                <Button type="submit" style={{ backgroundColor: "#00209F" }}>
                  Update Profile
                </Button>
              </div>
            </Form>
          </CardBody>
        </Card>
      </div>
    </React.Fragment>
  );
};

export default UserProfile;